import React, { useCallback, useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import clsx from "clsx";

import { racehorse360 } from "@tsg/1st-grpc-web-rh360";

import Select from "@material-ui/core/Select";
import MenuItem from "@material-ui/core/MenuItem";
import useMediaQuery from "@material-ui/core/useMediaQuery";
import { Theme } from "@material-ui/core/styles";

import EditButton from "./EditButton";
import ConfirmAndCancelButtons from "./CofirmAndCancelButtons";
import Loader from "../Loader";
import { useLoggedInUser } from "../LoggedInUserProvider";
import { EMPTY_STRING, NOT_ACCESSIBLE } from "common/constants";
import Breakpoints from "common/breakpoints";
import { setInfiniteScrollMode } from "store/actions/queriesConfig";

import useStyles from "./styles";

export interface IPickerOption {
  id: string;
  name: string;
}

export interface IOptionPickerProps {
  horse: racehorse360.IHorse;
  options: IPickerOption[];
  selectedFacilityId: string;
  optionId: string;
  onConfirm: (optionId: string) => Promise<void>;
  className?: string;
  startActions?: boolean;
  endActions?: boolean;
  missingValueSign?: string;
  iconVariant?: "default" | "map";
  isLoading?: boolean;
  isEditable?: boolean;
}

const MOBILE_LOADER_SIZE = 16;
const DESKTOP_LOADER_SIZE = 20;

export const EMPTY_OPTION: IPickerOption = {
  id: "emptyOptionId",
  name: ""
};

export const transformDataToIOption = (
  data: racehorse360.IBarn[]
): IPickerOption[] => data.map(item => ({ id: item.id, name: item.name }));

const OptionPicker = (props: IOptionPickerProps) => {
  const {
    options,
    className,
    horse,
    selectedFacilityId,
    optionId,
    onConfirm,
    startActions = false,
    endActions = false,
    isLoading = false,
    isEditable = true,
    missingValueSign = NOT_ACCESSIBLE,
    iconVariant = "default"
  } = props;

  const classes = useStyles();
  const dispatch = useDispatch();
  const { currentUser } = useLoggedInUser();
  const matchesDownXS420 = useMediaQuery((theme: Theme) =>
    theme.breakpoints.down(Breakpoints.XS_420)
  );

  const [isEditMode, setIsEditMode] = useState(false);
  const [selectValue, setSelectValue] = useState<string>(EMPTY_STRING);

  const loaderSize = matchesDownXS420
    ? MOBILE_LOADER_SIZE
    : DESKTOP_LOADER_SIZE;
  const isHorseAtSelectedFacility =
    selectedFacilityId === horse.currentFacility?.id;
  let cellContent: string;

  if (isHorseAtSelectedFacility) {
    cellContent =
      options.find(opt => opt.id === optionId)?.name || missingValueSign;
  } else {
    cellContent = horse.currentFacility?.code || missingValueSign;
  }

  const canEdit = [
    isEditable,
    !currentUser.isRegulatoryVet,
    isHorseAtSelectedFacility,
    options.length
  ].every(Boolean);
  const shouldShowActionsAtStart = [canEdit, startActions].every(Boolean);
  const shouldShowActionsAtEnd = [canEdit, endActions].every(Boolean);

  const handleOpenEditMode = () => {
    if (!canEdit) {
      return;
    }

    setSelectValue(optionId);
    dispatch(setInfiniteScrollMode(false));
    setIsEditMode(true);
  };

  const handleCloseEditMode = useCallback(() => {
    setIsEditMode(false);
    dispatch(setInfiniteScrollMode(true));
  }, [dispatch]);

  useEffect(() => {
    if (!canEdit && isEditMode) {
      handleCloseEditMode();
    }
  }, [canEdit, handleCloseEditMode, isEditMode]);

  const handleChange = event => {
    setSelectValue(event.target.value);
  };

  const handleConfirmValue = () => {
    if (selectValue === optionId) {
      handleCloseEditMode();

      return;
    }

    onConfirm(selectValue)
      .then(() => {
        handleCloseEditMode();
      })
      .catch(error => {
        console.error(error.toString());
      });
  };

  const renderContent = () => {
    if (!isEditMode) {
      return (
        <>
          <div>
            {shouldShowActionsAtStart && (
              <EditButton variant={iconVariant} onClick={handleOpenEditMode} />
            )}
          </div>
          <span>{cellContent}</span>
          <div>
            {shouldShowActionsAtEnd && (
              <EditButton variant={iconVariant} onClick={handleOpenEditMode} />
            )}
          </div>
        </>
      );
    }

    return (
      <>
        {shouldShowActionsAtStart && (
          <ConfirmAndCancelButtons
            onConfirm={handleConfirmValue}
            onCancel={handleCloseEditMode}
          />
        )}
        {isLoading && <Loader className={classes.loader} size={loaderSize} />}
        {!isLoading && (
          <Select
            className={classes.selectWrapper}
            variant={"outlined"}
            classes={{
              root: classes.root,
              select: classes.select,
              iconOutlined: classes.iconOutlined
            }}
            MenuProps={{
              getContentAnchorEl: null,
              classes: {
                paper: classes.paper,
                list: classes.selectList
              }
            }}
            value={selectValue}
            onChange={handleChange}
            onBlur={handleCloseEditMode}
          >
            {[EMPTY_OPTION, ...options].map(item => (
              <MenuItem key={item.id} value={item.id}>
                {item.name}
              </MenuItem>
            ))}
          </Select>
        )}
        {shouldShowActionsAtEnd && (
          <ConfirmAndCancelButtons
            onConfirm={handleConfirmValue}
            onCancel={handleCloseEditMode}
          />
        )}
      </>
    );
  };

  return (
    <div
      className={clsx(className, classes.container, {
        editMode: isEditMode,
        fadeOut: !horse.currentFacility
      })}
    >
      {renderContent()}
    </div>
  );
};

export default React.memo(OptionPicker);
